import {
  AfterViewChecked,
  AfterViewInit,
  booleanAttribute,
  Component,
  computed,
  ElementRef,
  HostBinding,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  viewChild,
  viewChildren,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, fromEvent, Subject, takeUntil } from 'rxjs';
import { Color, Rounded, Size, Variant } from '../common/types';
import { SegmentOptionComponent } from './option/segment-option.component';

export type SegmentOption = {
  label: string;
  value: string;
  icon?: string;
  count?: number;
  height?: Size;
  renderItem?: any;
  badge?: {
    label: string;
    color: Color;
    size: Size;
    rounded: Rounded;
    variant: Variant;
  };
};

@Component({
  selector: 'lepi-segment',
  templateUrl: './segment.component.html',
  styleUrls: ['./segment.component.css'],
  standalone: true,
  imports: [SegmentOptionComponent],
})
export class Segment
  implements OnInit, AfterViewChecked, AfterViewInit, OnDestroy
{
  private destroy$ = new Subject<void>();
  @HostBinding('class') get hostClass() {
    if (this.scrollEnabled()) {
      return 'w-full';
    }

    return '';
  }

  readonly elementRef = inject(ElementRef);

  container = viewChild<ElementRef<HTMLElement>>('container');
  indicator = viewChild<ElementRef<HTMLElement>>('indicator');
  items = viewChildren<SegmentOptionComponent>('item');

  color = input<Color>('primary');
  $options = input<string[] | SegmentOption[]>([], { alias: 'options' });

  options = computed<SegmentOption[]>(() => {
    if (this.$options().length === 0) return [];

    return this.$options().map(option => {
      if (typeof option === 'string') {
        return {
          label: option,
          value: option,
        };
      }

      return option;
    });
  });

  value = model<string>(this.options()[0]?.value);
  value$ = toObservable(this.value);

  scrollEnabled = input<boolean, string>(false, {
    transform: booleanAttribute,
  });

  ngOnInit(): void {
    if (!this.value()) {
      this.value.set(this.options()[0].value);
    }
  }

  ngAfterViewInit(): void {
    this.value$.subscribe({
      next: value => {
        if (this.container() && value && this.scrollEnabled()) {
          // setTimeout 안걸면 scrollBy가 안먹힘
          setTimeout(() => {
            const left = this.getTranslateX(
              this.options().findIndex(item => item.value === value)
            );
            this.container()!.nativeElement.scrollBy({
              left,
            });
          }, 100);
        }
      },
    });

    // window resize 시 indicator 위치 재계산
    fromEvent(window, 'resize')
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => {
        this.transform();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    this.value$.subscribe(value => {
      if (this.items() && this.indicator() && value) {
        this.transform();
      }
    });
  }

  handleClick(option: SegmentOption) {
    this.value.set(option.value);
  }

  transform() {
    const index = this.options().findIndex(item => item.value === this.value());
    if (index === -1) return;
    this.indicator()!.nativeElement.style.width = `${
      this.items()[index].elementRef.nativeElement!.offsetWidth
    }px`;
    const translateX = this.getTranslateX(index);
    this.indicator()!.nativeElement.style.transform = `translateX(${translateX}px)`;
  }

  getTranslateX(index: number) {
    return this.items()
      .slice(0, index)
      .reduce((acc, cur) => acc + cur.elementRef.nativeElement.offsetWidth, 0);
  }
}
